import type {
  TomVendingDailySummaryCashRow,
  TomVendingDailySummaryFillRow,
  TomVendingDailySummaryHeader,
  TomVendingDailySummaryProductUsedRow,
  TomVendingInferredSaleRow,
  TomVendingReconciliationRow,
} from '@/hooks/useTomVendingReports';
import type {
  TomVendingRejectReportRow,
  TomVendingSiteVisitHistoryRecentRow,
} from '@/hooks/useTomVendingSiteVisitHistory';
import { downloadXlsx, dollarsFromCents, yesNo, type XlsxSheetSpec } from '@/utils/downloadXlsx';
import { formatVisitDateLabel } from '@/components/toms-vending/TomsVendingSiteVisitDetailContent';

export const TOMS_REPORT_EXPORT_MAX_ROWS = 10_000;

function fileSlug(raw: string): string {
  const s = raw.trim().replace(/[^a-zA-Z0-9]+/g, '-').replace(/^-|-$/g, '');
  return s.slice(0, 40) || 'report';
}

function machineLabel(assetId: string | null | undefined, vendingMachineId: number | null | undefined): string {
  if (vendingMachineId == null) return 'Site total';
  return assetId?.trim() || `#${vendingMachineId}`;
}

function rejectMachineLabel(row: TomVendingRejectReportRow): string {
  if (row.source === 'warehouse') return 'Warehouse';
  return machineLabel(row.machineAssetId, row.vendingMachineId);
}

function rejectStatusLabel(row: TomVendingRejectReportRow): string {
  if (row.source === 'warehouse') return '';
  if (row.visitStatus === 'open') return 'In progress';
  if (row.visitStatus === 'finalized') return 'Finalized';
  return row.visitStatus ?? '';
}

export function downloadDailySummaryXlsx(opts: {
  date: string;
  locationName: string;
  summary: TomVendingDailySummaryHeader;
  cashRows: TomVendingDailySummaryCashRow[];
  productUsed: TomVendingDailySummaryProductUsedRow[];
  fills: TomVendingDailySummaryFillRow[];
}): void {
  const { date, locationName, summary, cashRows, productUsed, fills } = opts;
  const billsTotal = cashRows.reduce((s, r) => s + r.billsCents, 0);
  const coinsTotal = cashRows.reduce((s, r) => s + r.coinsCents, 0);
  const cashTotal = cashRows.reduce((s, r) => s + r.totalCents, 0);
  const sheets: XlsxSheetSpec[] = [
    {
      name: 'Summary',
      headers: ['Metric', 'Value'],
      rows: [
        ['Location', locationName],
        ['Visit date', formatVisitDateLabel(date)],
        ['Visits', summary.visitCount],
        ['Locations', summary.locationCount],
        ['Cash collected', dollarsFromCents(summary.totalCashCents)],
        ['Units used', summary.totalUnitsSold],
        ['Retail (used)', dollarsFromCents(summary.totalRetailCents)],
        ['Units filled', summary.totalFillQty],
        ['Rejected', summary.totalRejectQty],
        ['Recycled', summary.totalRecycleQty],
      ],
    },
    {
      name: 'Money',
      headers: ['Visit date', 'Location', 'Machine', 'Bills', 'Coins', 'Total', 'Notes', 'Status', 'Visit #'],
      rows: [
        ...cashRows.map((r) => [
          formatVisitDateLabel(r.visitDate),
          r.locationName?.trim() || `Location ${r.locationTomVendingId}`,
          machineLabel(r.assetId, r.vendingMachineId),
          dollarsFromCents(r.billsCents),
          dollarsFromCents(r.coinsCents),
          dollarsFromCents(r.totalCents),
          r.notes ?? '',
          r.cashSource === 'pending' ? 'Open visit' : 'Finalized',
          r.siteVisitId,
        ]),
        ...(cashRows.length > 0
          ? [
              [
                'Total',
                '',
                '',
                dollarsFromCents(billsTotal),
                dollarsFromCents(coinsTotal),
                dollarsFromCents(cashTotal),
                '',
                '',
                '',
              ],
            ]
          : []),
      ],
      currencyCols: [3, 4, 5],
    },
    {
      name: 'Product used',
      headers: ['UPC', 'Product', 'Units used', 'Retail'],
      rows: productUsed.map((r) => [
        r.upc,
        r.itemName ?? '',
        r.unitsSold,
        dollarsFromCents(r.retailCents),
      ]),
      currencyCols: [3],
    },
    {
      name: 'Fills',
      headers: ['UPC', 'Product', 'Filled', 'Rejected', 'Recycled'],
      rows: fills.map((r) => [r.upc, r.itemName ?? '', r.fillQty, r.rejectQty, r.recycleQty]),
    },
  ];
  downloadXlsx(`toms-daily-summary-${fileSlug(locationName)}-${date}.xlsx`, sheets);
}

export function downloadSiteVisitsXlsx(
  rows: TomVendingSiteVisitHistoryRecentRow[],
  dateFrom: string,
  dateTo: string,
): void {
  downloadXlsx(`toms-site-visits-${dateFrom}-to-${dateTo}.xlsx`, [
    {
      name: 'Site visits',
      headers: ['Visit date', 'Location', 'User', 'Machines', 'Cash collected', 'Visit #'],
      rows: rows.map((r) => [
        formatVisitDateLabel(r.visitDate),
        r.locationName?.trim() || `Location ${r.locationTomVendingId}`,
        r.userDisplayName ?? '',
        r.machineSnapshotCount,
        dollarsFromCents(r.totalCashCollectedCents ?? 0),
        r.siteVisitId,
      ]),
      currencyCols: [4],
    },
  ]);
}

export function downloadRejectsXlsx(
  rows: TomVendingRejectReportRow[],
  dateFrom: string,
  dateTo: string,
): void {
  downloadXlsx(`toms-rejects-${dateFrom}-to-${dateTo}.xlsx`, [
    {
      name: 'Rejects',
      headers: ['Date', 'Location', 'Source', 'Machine', 'Product', 'UPC', 'Qty', 'Recorded by', 'Status', 'Visit #'],
      rows: rows.map((r) => [
        formatVisitDateLabel(r.visitDate),
        r.locationName?.trim() || `Location ${r.locationTomVendingId}`,
        r.source === 'warehouse' ? 'Warehouse' : 'Site visit',
        rejectMachineLabel(r),
        r.itemName ?? '',
        r.upc,
        r.rejectQty,
        r.recordedByDisplayName ?? '',
        rejectStatusLabel(r),
        r.siteVisitId ?? '',
      ]),
    },
  ]);
}

export function downloadInferredSalesXlsx(rows: TomVendingInferredSaleRow[], dateFrom: string, dateTo: string): void {
  downloadXlsx(`toms-inferred-sales-${dateFrom}-to-${dateTo}.xlsx`, [
    {
      name: 'Inferred sales',
      headers: [
        'Period end',
        'Period start',
        'Location',
        'Machine',
        'UPC',
        'Product',
        'Units sold',
        'Unit price',
        'Unit COGS FIFO',
        'Retail',
        'Margin',
        'Missing price',
        'Missing COGS',
      ],
      rows: rows.map((r) => [
        r.periodEndDate,
        r.periodStartDate ?? '',
        r.locationName,
        r.assetId?.trim() || `#${r.vendingMachineId}`,
        r.upc,
        r.itemName ?? '',
        r.unitsSold,
        dollarsFromCents(r.unitPriceCents),
        dollarsFromCents(r.unitCostCents),
        dollarsFromCents(r.retailCents),
        dollarsFromCents(r.marginCents),
        yesNo(r.missingPrice),
        yesNo(r.missingCost),
      ]),
      currencyCols: [7, 8, 9, 10],
    },
  ]);
}

export function downloadReconciliationXlsx(
  rows: TomVendingReconciliationRow[],
  dateFrom: string,
  dateTo: string,
): void {
  downloadXlsx(`toms-reconciliation-${dateFrom}-to-${dateTo}.xlsx`, [
    {
      name: 'Reconciliation',
      headers: [
        'Visit date',
        'Location',
        'Machine',
        'Expected retail',
        'Cash collected',
        'Variance',
        'Var %',
        'Missing prices',
        'Missing cash',
        'Visit #',
      ],
      rows: rows.map((r) => [
        formatVisitDateLabel(r.visitDate),
        r.locationName,
        machineLabel(r.assetId, r.vendingMachineId),
        dollarsFromCents(r.expectedRetailCents),
        r.missingCash ? null : dollarsFromCents(r.cashCollectedCents),
        dollarsFromCents(r.varianceCents),
        r.variancePct,
        yesNo(r.missingPrices),
        yesNo(r.missingCash),
        r.siteVisitId,
      ]),
      currencyCols: [3, 4, 5],
    },
  ]);
}
